'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { FlameS } from '@lichtblick/photon/icons/energy/flame-s';
import { FlashS } from '@lichtblick/photon/icons/energy/flash-s';
import { Checkbox } from '@lichtblick/photon/molecules/checkbox';
import { useTracker } from '@lichtblick/tracker';
import { useRef, useState } from 'react';
import { buildUrl } from '../../helpers/links';
import { CityInput } from './CityInput';
import { CalculatorLayout } from './calculator-layout';
import styles from './calculator-layout.module.scss';
import { ELECTRICITY_USAGE, PEOPLE_LIMIT, squareMeterLimit, usageLimitElectricity, usageLimitGas } from './constants';
import { ElectrictyUsageInput } from './ElectricityUsageInput';
import { GasUsageInput } from './GasUsageInput';
import { CalculatorTranslations } from './translations';
import { UsageModal } from './UsageModal';
import { useVpCodes } from './useVpCodes';
export const HouseholdCalculator = ({ _uid, checkoutUrl, chip, directSalesPartnerCode, headline, isActionTriggerDisabled, powerCloudCampaignId, productAType, productBSelected, productBType, useDirectSalesPartnerCodeSitewide })=>{
    const { computeVpCodes } = useVpCodes();
    const { trackCustomEvent } = useTracker();
    const gasUsageRef = useRef(null);
    const isElectricity = productAType === 'electricity';
    const electricityUsageRef = useRef(null);
    const [isGasUsageModalOpen, setIsGasUsageModalOpen] = useState(false);
    const [isElectricityUsageModalOpen, setIsElectricityUsageModalOpen] = useState(false);
    const [isProductBSelected, setIsProductBSelected] = useState(Boolean(productBSelected));
    const [values, setValues] = useState({
        peopleCount: PEOPLE_LIMIT.default,
        electricityUsage: String(ELECTRICITY_USAGE[0]),
        gasUsage: '4500',
        livingSpace: squareMeterLimit.default
    });
    const handleSubmit = (e)=>{
        e.preventDefault();
        if (electricityUsageRef.current && Number(electricityUsageRef.current.value) > usageLimitElectricity.max) {
            setIsElectricityUsageModalOpen(true);
            return;
        }
        if (gasUsageRef.current && Number(gasUsageRef.current.value) > usageLimitGas.max) {
            setIsGasUsageModalOpen(true);
            return;
        }
        const vpCodes = computeVpCodes(directSalesPartnerCode, useDirectSalesPartnerCodeSitewide);
        trackCustomEvent({
            eventName: 'tariff_calculator_show_tariffs',
            customProps: {
                product: isElectricity ? 'ÖkoStrom' : 'ÖkoGas',
                zip_code: values?.postcode,
                ...isElectricity && {
                    people_counter: values?.peopleCount
                },
                ...!isElectricity && {
                    living_space: `${values?.livingSpace} m²`
                },
                consumption_calculator: isElectricity ? `${values?.electricityUsage} kWh/Jahr` : `${values?.gasUsage} kWh/Jahr`,
                bundle_product: isProductBSelected
            }
        });
        const params = new URLSearchParams({
            ...values?.city && {
                ort: values.city
            },
            ...values?.postcode && {
                plz: values.postcode
            },
            ...(productAType === 'electricity' || isProductBSelected) && values?.electricityUsage && {
                strom: values.electricityUsage
            },
            ...(productAType === 'gas' || isProductBSelected) && values?.gasUsage && {
                gas: values.gasUsage
            },
            ...powerCloudCampaignId && {
                cam: powerCloudCampaignId
            },
            ...vpCodes?.vp && {
                vp: vpCodes.vp
            },
            ...vpCodes?.uvp && {
                uvp: vpCodes.uvp
            }
        });
        location.assign(`${(checkoutUrl && buildUrl(checkoutUrl)) ?? '/checkout/'}?${params.toString()}`);
    };
    const electricityInput = /*#__PURE__*/ _jsx(ElectrictyUsageInput, {
        onChange: (electricityUsage, peopleCount)=>setValues((prev)=>({
                    ...prev,
                    electricityUsage,
                    peopleCount
                })),
        usageRef: electricityUsageRef,
        children: /*#__PURE__*/ _jsx(UsageModal, {
            isOpen: isElectricityUsageModalOpen,
            route: "/gewerbestrom",
            setIsOpen: setIsElectricityUsageModalOpen
        })
    });
    const gasInput = /*#__PURE__*/ _jsx(GasUsageInput, {
        onChange: (value, livingSpace)=>setValues((prev)=>({
                    ...prev,
                    gasUsage: value,
                    livingSpace: livingSpace
                })),
        usageRef: gasUsageRef,
        children: /*#__PURE__*/ _jsx(UsageModal, {
            isOpen: isGasUsageModalOpen,
            route: "/gewerbegas",
            setIsOpen: setIsGasUsageModalOpen
        })
    });
    return /*#__PURE__*/ _jsx(CalculatorLayout, {
        chip: chip,
        "data-testid": `calculator-${isElectricity ? 'electricity' : 'gas'}`,
        headerColor: isElectricity ? 'LightGreen' : 'LightAqua',
        headline: headline,
        icon: isElectricity ? /*#__PURE__*/ _jsx(FlashS, {}) : /*#__PURE__*/ _jsx(FlameS, {}),
        id: _uid,
        isActionTriggerDisabled: isActionTriggerDisabled,
        children: /*#__PURE__*/ _jsxs("form", {
            className: styles['calculator-content'],
            onSubmit: handleSubmit,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles['calculator-input-row'],
                    "data-testid": isElectricity ? 'electricityCalculatorGroup' : 'gasCalculatorGroup',
                    children: [
                        isElectricity ? electricityInput : gasInput,
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(CityInput, {
                                onChange: (city, postcode)=>{
                                    setValues({
                                        ...values,
                                        city,
                                        postcode
                                    });
                                }
                            })
                        })
                    ]
                }),
                productBType && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(Checkbox, {
                                label: /*#__PURE__*/ _jsx(Checkbox.Label, {
                                    children: isElectricity ? 'Gas hinzufügen' : 'ÖkoStrom hinzufügen'
                                }),
                                children: /*#__PURE__*/ _jsx(Checkbox.Checkbox, {
                                    checked: isProductBSelected,
                                    onChange: ()=>setIsProductBSelected(!isProductBSelected)
                                })
                            })
                        }),
                        isProductBSelected && /*#__PURE__*/ _jsx("div", {
                            className: styles['calculator-input-row'],
                            "data-testid": isElectricity ? 'gasCalculatorGroup' : 'electricityCalculatorGroup',
                            children: isElectricity ? gasInput : electricityInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(BoxCTA, {
                    as: "button",
                    "data-testid": "calc-submit",
                    type: "submit",
                    wide: true,
                    "data-dd-action-name": `Calculator submit: ${isElectricity ? 'ÖkoStrom' : 'Gas'}${isProductBSelected ? isElectricity ? ' + Gas' : ' + ÖkoStrom' : ''}`,
                    children: CalculatorTranslations.submitLabel
                })
            ]
        })
    });
};

//# sourceMappingURL=HouseholdCalculator.js.map